import cn from 'classnames';
import { getMapFromStack, INVISIBLE_ERROR, selectDisplayedOrAllGroupQuestions } from 'components-machine';
import { isNumber } from 'lodash';
import React, { createContext, ReactNode, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { WebviewContext } from 'src/store/WebviewContextWrapper';
import usePrevious from '../../hooks/usePrevious';
import { ICState } from '../../types/domain';
import { Poll as PollType } from '../../types/poll.domain';
import { getUrlParam } from '../../utils/get-url-param';
import { inIframe, isPollsSite } from '../../utils/location';
import { notifyParent } from '../../utils/notifyIframe';
import { notifyIframeHeight } from '../../utils/notifyIframeHeight';
import styles from './poll.module.css';
import { useQuestionTitleContext } from './QuestionTitleContext';
import { useTimerContext } from './TimerContext';

export function PollRenderAutosizeScrollContainer({
  children,
  icState,
  poll,
}: {
  icState: ICState;
  poll: PollType;
  children: ReactNode;
}) {
  const contentRef = useRef<HTMLDivElement>(null);

  const { scrollTo } = usePollAutoscrollOnFeedback({ icState, poll, contentRef });

  useIframeResizeNotifier(contentRef);

  const { invokeDeeplinkCtaHref } = useContext(WebviewContext);

  /** Для webview необходимо открывать ссылки в браузере */
  const clickByLinkInWebview = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      document &&
      poll.template === 'webview' &&
      Object.prototype.isPrototypeOf.call(Object.getPrototypeOf(document.createElement('a')), e.target)
    ) {
      const href = (e.target as unknown as HTMLAnchorElement).href;

      void invokeDeeplinkCtaHref(href);
      e.preventDefault();
    }
  };

  const { timerIsShown, timerHeight } = useTimerContext();

  const heightGrow = getUrlParam('height_grow') !== 'off';
  const rootStyle: React.CSSProperties = {
    height: heightGrow ? (timerIsShown ? `calc(100% - ${timerHeight}px)` : '100%') : 'auto',
    position: 'relative',
  };

  if (!timerIsShown) rootStyle.transition = 'height 5s ease-out';

  const className = cn(styles.content, {
    [c(styles.scrollable)]: !isPollsSite(),
  });

  return (
    <ScrollContextProvider value={{ scrollTo }}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={clickByLinkInWebview} ref={contentRef} id="content" style={rootStyle} className={className}>
        {children}
      </div>
    </ScrollContextProvider>
  );
}

function useIframeResizeNotifier(contentRef: React.RefObject<HTMLDivElement>) {
  const hideScroll = getUrlParam('hide_scroll') !== 'off';

  const { height } = useResizeDetector({ targetRef: contentRef as React.MutableRefObject<HTMLElement> });

  useEffect(() => {
    if (inIframe() && hideScroll) {
      const content = contentRef.current;
      if (content && content.parentElement) {
        if (content.scrollHeight <= content.clientHeight) {
          content.parentElement.style.overflowY = 'hidden';
        } else {
          content.parentElement.style.overflowY = 'auto';
        }
      }

      notifyIframeHeight();
    }
  }, [height]);

  useEffect(() => {
    const heightResizeObserver = new ResizeObserver(() => {
      notifyIframeHeight();
    });

    const heightMutationObserver = new MutationObserver(() => {
      notifyIframeHeight();
    });

    if (contentRef.current && inIframe({ ignoreForce: true })) {
      heightResizeObserver.observe(contentRef.current, { box: 'border-box' });
      heightMutationObserver.observe(contentRef.current, { childList: true, subtree: true });
    }

    return () => {
      heightResizeObserver.disconnect();
      heightMutationObserver.disconnect();
    };
  }, []);
}

// TODO: вынести реагирование на изменение в машину
function usePollAutoscrollOnFeedback({
  icState,
  poll,
  contentRef,
}: {
  icState: ICState;
  poll: PollType;
  contentRef: React.RefObject<HTMLDivElement>;
}) {
  const { questionId, groupId, finalId, answersStack } = icState;

  const prevQuestionId = usePrevious(questionId);
  const prevGroupId = usePrevious(groupId);
  const prevFinalId = usePrevious(finalId);
  const currentGroup = poll.groups.find(group => group.id === groupId);

  const answersMap = useMemo(() => getMapFromStack(answersStack), [answersStack]);

  const entityWithError = (() => {
    const hasVisibleError = (error?: string) => error && error !== INVISIBLE_ERROR;
    const checkErrorInAnswersMap = (questionId: string) => hasVisibleError(answersMap[questionId]?.errorMessage);

    if (groupId) {
      const questionWithErrorId =
        selectDisplayedOrAllGroupQuestions(poll, groupId, icState.answersStack).find(question =>
          checkErrorInAnswersMap(question.id),
        )?.id ?? null;

      return questionWithErrorId ? { questionId: questionWithErrorId, groupId } : null;
    }

    return questionId && checkErrorInAnswersMap(questionId) ? { questionId } : null;
  })();

  const { getTitleRef } = useQuestionTitleContext();

  const scrollTo = (topOrCb: number | ((scrollElement: HTMLElement) => number)) => {
    const onPollsSite = isPollsSite({ ignoreForce: true });
    const scrollElement = onPollsSite ? document.documentElement : contentRef.current;

    if (!scrollElement) return;

    const top = isNumber(topOrCb) ? topOrCb : topOrCb(scrollElement);
    scrollElement.scrollTo({ top, ...(onPollsSite && { behavior: 'smooth' }) });

    if (inIframe({ ignoreForce: true })) {
      // таймаут для того, чтобы событие было вызвано позже, чем возможное событие survey_iframe_height
      setTimeout(() => {
        notifyParent({ type: 'survey_iframe_requests_scroll', top });
      }, 0);
    }
  };

  const scrollToEntityTitle = (entity: { questionId: string; groupId?: string | null }) => {
    const entityTitle = getTitleRef(entity.groupId ?? entity.questionId);

    if (!entityTitle) return;

    const scrollOffset = 30;
    scrollTo(scrollElement => entityTitle.getBoundingClientRect().top + scrollElement.scrollTop - scrollOffset);
  };

  useEffect(() => {
    if (entityWithError) {
      scrollToEntityTitle(entityWithError);
    }
  }, [entityWithError]);

  useLayoutEffect(() => {
    const isScreenChanged = prevQuestionId !== questionId || prevGroupId !== groupId || prevFinalId !== finalId;

    let hasFeedback: boolean;
    if (groupId) {
      const groupQuestionIds = selectDisplayedOrAllGroupQuestions(poll, groupId, icState.answersStack).map(
        question => question.id,
      );
      hasFeedback = groupQuestionIds.some(questionId => answersMap[questionId]?.isAnswered);
    } else {
      hasFeedback = questionId ? !!answersMap[questionId]?.isAnswered : false;
    }

    if (!(isScreenChanged || hasFeedback)) return;

    const hasSequentialDisplay = !!currentGroup?.data.hasSequentialDisplay;

    if (hasSequentialDisplay && hasFeedback) {
      const lastQuestionOnStack = answersStack.at(-1);
      const lastQuestionId = lastQuestionOnStack?.id;

      if (lastQuestionId) {
        scrollToEntityTitle({ questionId: lastQuestionId });
      }
    } else {
      scrollTo(0);
    }
  }, [prevQuestionId, questionId, prevGroupId, groupId, prevFinalId, finalId, Object.keys(answersMap).length]);

  return { scrollTo };
}

type ScrollContextValue = ReturnType<typeof usePollAutoscrollOnFeedback>;
const ScrollContext = createContext<ScrollContextValue | null>(null);

const ScrollContextProvider = ({ value, children }: { value: ScrollContextValue; children: React.ReactNode }) => {
  return <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>;
};

export const useScrollContext = () => {
  const value = useContext(ScrollContext);
  if (!value) throw new Error('ScrollContext is required!');
  return value;
};
